import { z } from 'zod';

/** Read-only AI extraction. Saving a reviewed document uses the canonical editors. */
export const posAiDocumentTypeSchema = z.enum(['ddt_passivo', 'fattura_passiva', 'ordine_fornitore']);
export type PosAiDocumentType = z.infer<typeof posAiDocumentTypeSchema>;

const confidentString = z.object({ value: z.string().nullable(), confidence: z.number() });
const confidentNumber = z.object({ value: z.number().nullable(), confidence: z.number() });
const nullableString = z.string().nullable();
const nullableNumber = z.number().nullable();

export const posAiParsedMatchedProductSchema = z.object({
  id: z.string(),
  name: nullableString,
  sku: nullableString,
  ean: nullableString,
  cost_price: nullableNumber,
  serial_mode: z.string(),
  match_type: z.enum(['ean', 'sku', 'semantic']).nullable(),
  match_score: z.number(),
  cost_variance: nullableNumber,
});
export type PosAiParsedMatchedProduct = z.infer<typeof posAiParsedMatchedProductSchema>;

export const posAiParsedSuggestionSchema = z.object({
  id: z.string(),
  name: z.string(),
  sku: nullableString,
  ean: nullableString,
  similarity: z.number(),
});
export type PosAiParsedSuggestion = z.infer<typeof posAiParsedSuggestionSchema>;

export const posAiParsedLineSchema = z.object({
  description: z.string(),
  sku: confidentString,
  ean: confidentString,
  quantity: confidentNumber,
  unit_price: confidentNumber,
  unit_of_measure: nullableString,
  vat_rate: nullableNumber,
  vat_nature: nullableString.optional(),
  riferimento_normativo: nullableString.optional(),
  discount_percent: nullableNumber,
  line_total: nullableNumber,
  quantity_ordered: nullableNumber,
  quantity_shipped: nullableNumber,
  serials: z.array(z.string()),
  matched_product: posAiParsedMatchedProductSchema.nullable(),
  suggestions: z.array(posAiParsedSuggestionSchema),
});
export type PosAiParsedLine = z.infer<typeof posAiParsedLineSchema>;

export const posAiParsedHeaderSchema = z.object({
  // Legacy names retained for all document types; for orders these are the order number/date.
  ddt_number: confidentString,
  ddt_date: confidentString,
  supplier_name: confidentString,
  supplier_vat: confidentString,
  matched_supplier_id: nullableString,
  total_amount: nullableNumber,
  total_vat: nullableNumber,
  currency: nullableString,
  supplier_sdi: nullableString,
  supplier_pec: nullableString,
  supplier_address: nullableString,
  supplier_city: nullableString,
  supplier_zip: nullableString,
  supplier_province: nullableString,
  supplier_country: nullableString,
  supplier_email: nullableString,
  supplier_phone: nullableString,
  carrier_name: nullableString,
  recipient_name: nullableString,
  recipient_address: nullableString,
  order_ref: nullableString,
  expected_delivery: nullableString.optional(),
  payment_terms: nullableString.optional(),
  notes: nullableString.optional(),
});
export type PosAiParsedHeader = z.infer<typeof posAiParsedHeaderSchema>;

export const posAiParsedOpenOrderSchema = z.object({
  id: z.string(),
  order_number: z.string(),
  order_date: nullableString,
  status: z.string(),
  total: nullableNumber,
});
export type PosAiParsedOpenOrder = z.infer<typeof posAiParsedOpenOrderSchema>;

/** Failure responses may omit extraction details (e.g. no configured OCR provider). */
export const posAiDocumentParseResponseSchema = z.object({
  ok: z.boolean(),
  error: z.string().optional(),
  document_type: posAiDocumentTypeSchema.optional(),
  provider: z.string().optional(),
  source: z.enum(['ai_structured', 'regex_fallback']).optional(),
  pages: z.number().optional(),
  cost_estimate: nullableNumber.optional(),
  header: posAiParsedHeaderSchema.optional(),
  lines: z.array(posAiParsedLineSchema).optional(),
  open_purchase_orders: z.array(posAiParsedOpenOrderSchema).optional(),
  matched_purchase_order: posAiParsedOpenOrderSchema.nullable().optional(),
  raw_text: z.string().optional(),
  warnings: z.array(z.string()).optional(),
});
export type PosAiDocumentParseResponse = z.infer<typeof posAiDocumentParseResponseSchema>;